import React, { useState } from 'react';
import { styles } from './ShipmentProgress.css';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { JBBagIllustrated, JBBoxIllustrated } from '@jbhi-fi/lanyard-ui/components/Icons';
import { ProgressStatus } from '@jbhi-fi/apis/src/orderTrackingThemeApi/types';

type ShipmentProgressProps = {
  variant: 'delivery' | 'customerCollect';
  steps: Array<ProgressStatus>;
  shipmentStatus: ProgressStatus;
  hasWarning?: boolean;
};

export const ShipmentProgress = ({ variant, steps, shipmentStatus, hasWarning }: ShipmentProgressProps) => {
  const isDesktop = useBreakpoint('md', 'up');
  const iconSize = isDesktop ? 50 : 30;
  const [stepsRef, setStepsRef] = useState<HTMLDivElement | null>(null);

  const currentStepIndex = steps.findIndex(step => step === shipmentStatus);
  const isLastStep = currentStepIndex === steps.length - 1;

  // Because the first and last step labels are aligned to the left and right edges of the bar,
  // we can't just use a percentage width or the bar won't line up with the labels.
  let barFillWidth: number | string = 0;

  if (stepsRef) {
    const stepWidth = stepsRef?.children[0]?.getBoundingClientRect().width;
    const currentStepX = stepsRef?.children[currentStepIndex]?.getBoundingClientRect().x;
    const barX = stepsRef?.getBoundingClientRect().x;
    const currentStepRelativeX = currentStepX - barX;

    if (currentStepIndex === 0) {
      barFillWidth = iconSize * (isDesktop ? 1 : 1.5);
    } else if (isLastStep) {
      barFillWidth = `calc(100%)`;
    } else {
      barFillWidth = isDesktop ? currentStepRelativeX + stepWidth / 2.7 : currentStepRelativeX + stepWidth / 2;
    }
  }

  const getStepStatus = (index: number) => {
    if (index < currentStepIndex) {
      return 'complete';
    }
    if (index === currentStepIndex) {
      if (hasWarning) {
        return 'warning';
      } else {
        return 'current';
      }
    }
    return 'incomplete';
  };

  return (
    <div className={styles.container} data-testid="order-tracking-progress">
      <div className={styles.bar}>
        <div className={styles.barFill} style={{ width: barFillWidth }}>
          {variant === 'delivery' ? (
            <JBBoxIllustrated className={styles.icon({ hasWarning, isLastStep })} width={iconSize} height={iconSize} />
          ) : null}
          {variant === 'customerCollect' ? (
            <JBBagIllustrated className={styles.icon({ hasWarning, isLastStep })} width={iconSize} height={iconSize} />
          ) : null}
        </div>
      </div>
      <div ref={setStepsRef} className={styles.steps}>
        {steps.map((step, index) => {
          return (
            <div className={styles.step({ stepStatus: getStepStatus(index) })} key={index}>
              {step}
            </div>
          );
        })}
      </div>
    </div>
  );
};
